import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Modal, Checkbox, Button, Progress, Space, Typography, Alert, Collapse, Select, Input } from "antd";
import { SubjectRepository, SkosScheme, RdfTriplet, GraphExchangeRepository } from "../../redux";
import { lastElementCardURI } from "../../utils/uriUtil";
import { ntriplesToInsertData, buildDeleteSPARQL } from "../import/helper";

const { Text, Title } = Typography;

const OPT = {
  CARD: "card_asset",
  CLASS: "classification",
  RELATED: "relatedRefs",
};

const LABELS = {
  [OPT.CLASS]: "Включить в синхронизацию детальную информацию о концептах",
  [OPT.RELATED]: "Включить в синхронизацию детальную информацию о связанных элементах (при наличии)",
};

const defaultChecked = [OPT.CLASS, OPT.RELATED];

function getPurlServerFromIri(iri) {
  if (typeof iri !== "string" || !iri.trim()) return "";
  try {
    return new URL(iri).origin;
  } catch {
    return iri.trim();
  }
}

function replaceAllText(value, source, target) {
  if (typeof value !== "string" || !source) return value;
  return value.split(source).join(target);
}

function replacePurlInIRIs(IRIs, source, target) {
  if (!Array.isArray(IRIs) || !source) return IRIs;
  return IRIs.map((iri) => replaceAllText(iri, source, target));
}

function buildErrorMessage(err, fallback = "Ошибка синхронизации.") {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  if (err?.message) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return fallback;
  }
}

function getGraphValue(binding) {
  return binding?.graph?.value || binding?.g?.value || binding?.IRI?.value || binding?.value;
}

function SynchronizationConceptScheme({
  open,
  onClose,
  dispatch,
  IRI = null,
  graph = "http://localhost/sksa/graph",
}) {
  const [checked, setChecked] = useState(defaultChecked);
  const [graphs, setGraphs] = useState([]);
  const [selectedGraph, setSelectedGraph] = useState();
  const [isLoadingGraphs, setIsLoadingGraphs] = useState(false);

  const [deleteBeforeImport, setDeleteBeforeImport] = useState(false);
  const [replacePurl, setReplacePurl] = useState(false);
  const [sourcePurlServer, setSourcePurlServer] = useState("");
  const [targetPurlServer, setTargetPurlServer] = useState("");

  const [isSyncing, setIsSyncing] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState(null);

  const [logLines, setLogLines] = useState([]);
  const [logCollapsed, setLogCollapsed] = useState(true);

  const canPURLChange = !replacePurl || (!!sourcePurlServer.trim() && !!targetPurlServer.trim());
  const canSync = !!IRI && !!selectedGraph && !isSyncing && canPURLChange;

  useEffect(() => {
    if (!open) return;

    setChecked(defaultChecked);
    setDeleteBeforeImport(false);
    setReplacePurl(false);
    setSourcePurlServer(getPurlServerFromIri(IRI));
    setTargetPurlServer("");
    setGraphs([]);
    setSelectedGraph(undefined);
    resetRuntimeState();
    loadGraphs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, IRI]);

  function resetRuntimeState() {
    setIsSyncing(false);
    setIsDone(false);
    setProgress(0);
    setStatusText("");
    setError(null);
    setLogLines([]);
    setLogCollapsed(true);
  }

  function pushLog(line) {
    const ts = new Date().toISOString().slice(11, 19);
    setLogLines((prev) => [...prev, `[${ts}] ${line}`]);
  }

  async function loadGraphs() {
    if (!IRI) return;

    setIsLoadingGraphs(true);
    setError(null);
    try {
      const response = await dispatch(SkosScheme.inGraph(IRI));
      const bindings = response?.results?.bindings ?? [];
      const values = uniqueIRIs(bindings.map(getGraphValue));
      setGraphs(values);
      setSelectedGraph(values.find((item) => item !== graph) || values[0]);
    } catch (e) {
      setError(buildErrorMessage(e, "Не удалось получить список графов."));
    } finally {
      setIsLoadingGraphs(false);
    }
  }

  function buildTasks() {
    const selected = new Set(checked);
    return [
      {
        key: OPT.CARD,
        includeConcepts: selected.has(OPT.CLASS),
        includeRelated: selected.has(OPT.RELATED),
        info: { scheme: IRI, IRIs: [IRI] },
        id: `1_${lastElementCardURI(IRI)}`,
        title: `Получение данных ${lastElementCardURI(IRI)}`,
        run: () => SubjectRepository.exportSubject(graph, [IRI]),
      },
    ];
  }

  async function prepareTask(task) {
    if (!task.includeRelated && !task.includeConcepts) return task;

    pushLog(`Поиск связанных объектов для ${task.id}`);

    const related = task.includeRelated ? await RdfTriplet.loadSubject(task.info.scheme, graph) : null;
    const relatedIRIs = task.includeRelated
      ? related.bindings
          .filter((item) => item.predicat.value !== "http://www.w3.org/1999/02/22-rdf-syntax-ns#type")
          .filter((item) => item.object.type === "uri" && item.predicat.value !== "http://www.w3.org/2004/02/skos/core#topConceptOf")
          .map((item) => item.object.value)
      : [];

    const concepts = task.includeConcepts ? await RdfTriplet.loadObject(task.info.scheme, graph) : null;
    const conceptIRIs = task.includeConcepts
      ? concepts.bindings
          .filter((item) => item.predicat.value !== "http://www.w3.org/1999/02/22-rdf-syntax-ns#type")
          .filter((item) => item.predicat.value === "http://www.w3.org/2004/02/skos/core#inScheme")
          .map((item) => item.subject.value)
      : [];

    const IRIs = uniqueIRIs([task.info.scheme, ...relatedIRIs, ...conceptIRIs]);
    return {
      ...task,
      info: { ...task.info, IRIs },
      run: () => SubjectRepository.exportSubject(graph, IRIs),
    };
  }

  async function handleSynchronize() {
    if (!canSync) return;

    setError(null);
    setIsDone(false);
    setIsSyncing(true);
    setProgress(0);
    setStatusText("Подготовка синхронизации...");
    setLogCollapsed(false);
    pushLog(`Старт синхронизации из ${graph} в ${selectedGraph}.`);

    try {
      const tasks = buildTasks();
      const total = tasks.length;

      for (let i = 0; i < tasks.length; i += 1) {
        const preparedTask = await prepareTask(tasks[i]);
        setStatusText(`Экспорт: ${preparedTask.title}...`);
        pushLog(`Экспорт: ${preparedTask.title}`);
        setProgress(Math.round((i / total) * 40));

        const result = await preparedTask.run();
        const sourcePurl = sourcePurlServer.trim();
        const targetPurl = targetPurlServer.trim();
        const content = replacePurl ? replaceAllText(result, sourcePurl, targetPurl) : result;
        const importIRIs = replacePurl ? replacePurlInIRIs(preparedTask.info.IRIs, sourcePurl, targetPurl) : preparedTask.info.IRIs;

        setStatusText("Формирование SPARQL для импорта...");
        setProgress(50);

        const sparqlDelete = deleteBeforeImport ? await buildDeleteSPARQL(importIRIs, selectedGraph) : "";
        const sparqlInsert = await ntriplesToInsertData(content);
        const sparql = `${sparqlDelete}\n${sparqlInsert}`;

        setStatusText("Импорт в выбранный граф...");
        setProgress(75);
        pushLog(`Импорт в граф ${selectedGraph}`);

        const importResult = await GraphExchangeRepository.importSubjects(selectedGraph, sparql);
        if (importResult !== "OK") throw new Error("Ошибка выполнения SPARQL запроса.");

        setProgress(Math.round(((i + 1) / total) * 100));
      }

      setStatusText("Синхронизация завершена.");
      setProgress(100);
      setIsDone(true);
      pushLog("Синхронизация завершена.");
      setLogCollapsed(true);
    } catch (e) {
      setProgress(0);
      setStatusText("");
      setError(buildErrorMessage(e));
      pushLog(`Ошибка синхронизации: ${buildErrorMessage(e)}`);
      setLogCollapsed(false);
    } finally {
      setIsSyncing(false);
    }
  }

  function handleClose() {
    resetRuntimeState();
    onClose?.();
  }

  const footer = isDone ? (
    <Button type="primary" onClick={handleClose}>Закрыть</Button>
  ) : (
    <Space>
      <Button onClick={handleClose} disabled={isSyncing}>Отмена</Button>
      <Button type="primary" onClick={handleSynchronize} loading={isSyncing} disabled={!canSync}>
        Синхронизировать
      </Button>
    </Space>
  );

  const graphOptions = useMemo(
    () => graphs.map((item) => ({ label: item, value: item })),
    [graphs]
  );

  const logItems = [
    {
      key: "log",
      label: "Журнал выполнения",
      children: (
        <div
          style={{
            maxHeight: 220,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            fontSize: 12,
            lineHeight: 1.35,
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 10,
          }}
        >
          {logLines.length ? logLines.join("\n") : "Лог пуст."}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={<Title level={5} style={{ margin: 0 }}>Синхронизация</Title>}
      open={open}
      onCancel={handleClose}
      maskClosable={!isSyncing}
      closable={!isSyncing}
      destroyOnHidden={false}
      footer={footer}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space direction="vertical" size={6} style={{ width: "100%" }}>
          <Text strong>Граф для синхронизации:</Text>
          <Select
            showSearch
            value={selectedGraph}
            loading={isLoadingGraphs}
            options={graphOptions}
            onChange={setSelectedGraph}
            disabled={isSyncing || isDone}
            placeholder="Выберите граф"
            style={{ width: "100%" }}
          />
        </Space>

        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Text strong>Что синхронизировать:</Text>
          <Checkbox
            checked={checked.includes(OPT.CLASS)}
            onChange={(e) => toggleChecked(OPT.CLASS, e.target.checked, checked, setChecked)}
            disabled={isSyncing || isDone}
          >
            {LABELS[OPT.CLASS]}
          </Checkbox>
          <Checkbox
            checked={checked.includes(OPT.RELATED)}
            onChange={(e) => toggleChecked(OPT.RELATED, e.target.checked, checked, setChecked)}
            disabled={isSyncing || isDone}
          >
            {LABELS[OPT.RELATED]}
          </Checkbox>
        </Space>

        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Text strong>Опции импорта:</Text>
          <Checkbox checked disabled>
            Импорт справочника(ов)
          </Checkbox>
          <Checkbox checked={deleteBeforeImport} onChange={(e) => setDeleteBeforeImport(e.target.checked)} disabled={isSyncing || isDone}>
            Удалить перед импортом
          </Checkbox>
          <Checkbox checked={replacePurl} onChange={(e) => setReplacePurl(e.target.checked)} disabled={isSyncing || isDone}>
            Заменить PURL сервер
          </Checkbox>
          {replacePurl && (
            <Space direction="vertical" style={{ width: "100%" }}>
              <Input
                value={sourcePurlServer}
                onChange={(e) => setSourcePurlServer(e.target.value)}
                placeholder="Исходный PURL сервер"
                disabled={isSyncing || isDone}
              />
              <Input
                value={targetPurlServer}
                onChange={(e) => setTargetPurlServer(e.target.value)}
                placeholder="Целевой PURL сервер"
                disabled={isSyncing || isDone}
              />
            </Space>
          )}
        </Space>

        {(isSyncing || isDone) && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Progress percent={progress} />
            {statusText ? <Text>{statusText}</Text> : null}
            <Collapse
              items={logItems}
              activeKey={logCollapsed ? [] : ["log"]}
              onChange={(keys) => setLogCollapsed(!(Array.isArray(keys) ? keys.includes("log") : false))}
            />
          </Space>
        )}

        {error ? (
          <Alert type="error" showIcon message="Ошибка синхронизации" description={<Text>{error}</Text>} />
        ) : null}

        {isDone ? <Alert type="success" showIcon message="Синхронизация выполнена успешно." /> : null}
      </Space>
    </Modal>
  );
}

function toggleChecked(key, next, checked, setChecked) {
  const set = new Set(checked);
  if (next) set.add(key);
  else set.delete(key);
  setChecked(Array.from(set));
}

function uniqueIRIs(IRIs) {
  return [...new Set((Array.isArray(IRIs) ? IRIs : []).filter(Boolean))];
}

const mapStateToProps = (state, ownProps) => {
  const { vset } = state;
  return {
    ...ownProps,
    graph: vset.graph.signed,
  };
};

export default connect(mapStateToProps)(SynchronizationConceptScheme);
